import { useEffect, useState } from 'react'
import { Select, Space, Typography } from 'antd'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { SEARCH_DEBOUNCE_MS } from '@/shared/lib/localList'
import { fetchLeads, fetchTickets } from './crmApi'

const LIMIT = 20

interface RecordOption {
  id: string
  title: string
  detail: string | null
}

interface CrmRecordSelectProps {
  kind: 'ticket' | 'lead'
  value?: string | null
  onChange?: (value: string | null) => void
}

async function searchRecords(
  kind: CrmRecordSelectProps['kind'],
  search: string,
  signal: AbortSignal,
): Promise<RecordOption[]> {
  const filter = { search: search || undefined, includeClosed: true, page: 0, size: LIMIT }
  if (kind === 'ticket') {
    const page = await fetchTickets(filter, signal)
    return page.rows.map((row) => ({
      id: row.id,
      title: row.subject,
      detail: row.subscriberLogin ?? row.venueName ?? null,
    }))
  }
  const page = await fetchLeads(filter, signal)
  return page.rows.map((row) => ({ id: row.id, title: row.fullName, detail: row.companyName ?? null }))
}

export function CrmRecordSelect({ kind, value, onChange }: CrmRecordSelectProps) {
  const { t } = useTranslation()
  const [typed, setTyped] = useState('')
  const [term, setTerm] = useState('')
  const [picked, setPicked] = useState<RecordOption | null>(null)

  useEffect(() => {
    if (typed === term) return
    const timer = setTimeout(() => setTerm(typed), SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [typed, term])

  const filterKey = { picker: true, search: term }
  const list = useQuery({
    queryKey: kind === 'ticket' ? queryKeys.crm.tickets(filterKey) : queryKeys.crm.leads(filterKey),
    queryFn: ({ signal }) => searchRecords(kind, term.trim(), signal),
    staleTime: 30_000,
  })

  const rows = list.data ?? []
  const shown = picked && picked.id === value && !rows.some((row) => row.id === picked.id)
    ? [picked, ...rows]
    : rows

  return (
    <Select<string | null>
      allowClear
      showSearch
      value={value ?? null}
      onChange={(next) => {
        setPicked(shown.find((row) => row.id === next) ?? null)
        onChange?.(next ?? null)
      }}
      onSearch={setTyped}
      filterOption={false}
      onBlur={() => setTyped('')}
      loading={list.isFetching}
      placeholder={t(`crm.recordSelect.${kind}Placeholder`)}
      notFoundContent={t('crm.recordSelect.empty')}
      options={shown.map((row) => ({
        value: row.id,
        title: row.title,
        label: (
          <Space size={4} wrap>
            <Typography.Text strong>{row.title}</Typography.Text>
            {row.detail ? <Typography.Text type="secondary">{row.detail}</Typography.Text> : null}
          </Space>
        ),
      }))}
    />
  )
}
